import { Component, EventEmitter, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CreateNewParkingSpaceRequest, OrganizationDto, OrganizationService, ParkingAccessMode, ParkingEntryType, ParkingSpaceDto, ParkingSpaceService, ParkingSpaceType } from '../../../../api/gen';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AddressItemComponent } from '../../address-item/address-item.component';
import { AccessWindowFieldComponent, AccessWindowValue } from '../../../shared/access-window-field/access-window-field.component';
import { JJamesCardComponent } from 'shared-lib';
import { firstValueFrom } from 'rxjs';

/**
 * The EFSTA register is both halves or neither: one alone would pair a custom TL with the proxy's
 * default TT and open a register nobody has onboarded with a signature device.
 */
function fiscalRegisterPair(form: AbstractControl): ValidationErrors | null {
  const location = (form.get('fiscalTransactionLocation')?.value ?? '').trim();
  const terminal = (form.get('fiscalTransactionTerminal')?.value ?? '').trim();
  return (location === '') === (terminal === '') ? null : { fiscalRegisterPair: true };
}

@Component({
  selector: 'app-create-parking-space-initial',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    JJamesCardComponent,
    MatSnackBarModule,
    AddressItemComponent,
    AccessWindowFieldComponent
  ],
  templateUrl: './create-parking-space-initial.component.html',
  styleUrl: './create-parking-space-initial.component.scss'
})
export class CreateParkingSpaceInitialComponent {
  @Output() parkingSpaceCreated = new EventEmitter<ParkingSpaceDto>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;
  isSaving = signal(false);

  /** Optional restricted-access (visitor) window. Empty = no window. Applies to Closed spaces. */
  restrictedWindow = signal<AccessWindowValue>({ start: null, end: null, days: null });

  /** The restricted-access window only applies to Closed spaces, so only surface it for those. */
  get isClosedSpace(): boolean {
    return this.form.get('accessMode')?.value === ParkingAccessMode.Closed;
  }

  parkingSpaceTypes = Object.values(ParkingSpaceType);
  parkingAccessModes = Object.values(ParkingAccessMode);
  parkingEntryTypes = Object.values(ParkingEntryType);

  constructor(
    private fb: FormBuilder,
    private parkingSpaceService: ParkingSpaceService,
    private organizationService: OrganizationService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [ParkingSpaceType.Standard, Validators.required],
      address: [null, Validators.required],
      accessMode: [ParkingAccessMode.Open, Validators.required],
      entryType: [ParkingEntryType.FreeFlow, Validators.required],
      oldParkingSiteId: [null],
      // EFSTA EFR register (TL/TT pair). Blank both = the proxy's default register. Sent as a pair:
      // one half without the other would pair a custom TL with the default TT and open an unboarded
      // register. Both-or-neither is enforced by the fiscalRegisterPair validator.
      fiscalTransactionLocation: ['', [Validators.maxLength(46)]],
      fiscalTransactionTerminal: ['', [Validators.maxLength(46)]],
      // Empty = operator-governed. Set it and SSO logins at this space become members of that
      // organisation — the prerequisite for putting them in one of its pools later.
      externalOrganizationId: [null],
    }, { validators: fiscalRegisterPair });

    this.organizationService.organizationsFilterPost({ pageIndex: 0, pageSize: 200, includeDeleted: false })
      .subscribe({
        next: (response) => this.organizations.set(response?.items ?? []),
        // A failed lookup must not block creating the space — the picker just stays empty.
        error: () => this.organizations.set([])
      });
  }

  /** Organisations that can govern this site; empty selection means operator-governed. */
  organizations = signal<OrganizationDto[]>([]);

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const formValue = this.form.value;

    // Window only applies to Closed spaces — drop it otherwise so we don't persist an unused window.
    const window = this.isClosedSpace ? this.restrictedWindow() : { start: null, end: null, days: null };

    const request: CreateNewParkingSpaceRequest = {
      name: formValue.name,
      description: formValue.description,
      parkingSpaceType: formValue.type,
      address: formValue.address,
      accessMode: formValue.accessMode,
      entryType: formValue.entryType,
      oldParkingSiteId: formValue.oldParkingSiteId != null ? Number(formValue.oldParkingSiteId) : null,
      // Blank = no custom register (proxy default). The handler treats empty as null on create.
      fiscalTransactionLocation: formValue.fiscalTransactionLocation?.trim() || null,
      fiscalTransactionTerminal: formValue.fiscalTransactionTerminal?.trim() || null,
      externalOrganizationId: formValue.externalOrganizationId || null,
      restrictedAccessWindowStart: window.start,
      restrictedAccessWindowEnd: window.end,
      restrictedAccessWindowDays: window.days,
    };

    try {
      const newParkingSpace = await firstValueFrom(this.parkingSpaceService.parkingSpacePost(request));
      this.snackBar.open('Parking space created successfully.', 'Close', { duration: 3000 });
      this.parkingSpaceCreated.emit(newParkingSpace);
    } catch (error) {
      console.error('Error creating parking space:', error);
      this.snackBar.open('Failed to create parking space. Please try again.', 'Close', { duration: 5000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  onCancel() {
    this.cancel.emit();
  }
}
